'use client';

import { DefaultTextField } from '@/components/Formie/Fields/DefaultTextField';
import Icon from '@/components/Icon';
import Btn from '@/components/ui/Btn';
import Flex from '@/components/ui/Flex';
import { Alert, IconButton } from '@mui/material';
import { useTranslations } from 'next-intl';
import React from 'react';
import useProtectedEntry from './useProtectedEntry';

type LayoutProtectedPasswordFormProps = {
  entryId: string;
};

const LayoutProtectedPasswordForm = ({ entryId }: LayoutProtectedPasswordFormProps) => {
  const t = useTranslations('protected');

  const { isAuthorizing, isLoginError, isLoggingIn, isLoginSuccess, loginMessage, handleLogin } =
    useProtectedEntry(entryId);

  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleLogin(password);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  return isAuthorizing ? (
    <Flex direction="row">
      <Alert severity="info">{t('authorizing')}</Alert>
    </Flex>
  ) : (
    <Flex as="form" spacing="md" onSubmit={handleSubmit}>
      <Flex spacing="md" direction="row" cx={{ width: 'full' }}>
        <DefaultTextField
          disabled={isLoggingIn}
          error={isLoginError}
          label={t('password')}
          value={password}
          name="password"
          type={showPassword ? 'text' : 'password'}
          onChange={handleChange}
          sx={{ maxWidth: { lg: '60%' } }}
          InputProps={{
            endAdornment: (
              <>
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  <Icon name={showPassword ? 'eyeOff' : 'eye'} />
                </IconButton>
              </>
            ),
          }}
        />
        <Btn type="submit" disabled={isLoggingIn} rounded={false}>
          {t('submit')}
        </Btn>
      </Flex>
      {isLoggingIn ? (
        <Flex direction="row">
          <Alert severity="info">{t('loggingIn')}</Alert>
        </Flex>
      ) : loginMessage ? (
        <Flex direction="row">
          <Alert severity={isLoginSuccess ? 'success' : isLoginError ? 'error' : 'info'}>
            {loginMessage}
          </Alert>
        </Flex>
      ) : null}
    </Flex>
  );
};

export default LayoutProtectedPasswordForm;
